<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter, type LocationQueryValue } from "vue-router";

import SimpleButton from "@/components/ui/SimpleButton.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import type { EstimatorReportDto } from "@/dto";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { ProjectDetailSections, RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { useModalsStore } from "@/stores/modals";
import EstimatorReportComparison from "@/views/comparison/EstimatorReportComparison.vue";

const api = useApi();
const route = useRoute();
const router = useRouter();
const modal = useModalsStore();
const tenantName = computed(() => route.params.tenantName as string);
const projectName = computed(() => route.params.projectName as string);

const isLoading = ref(true);
const reports = ref<EstimatorReportDto[]>([]);
const selectedModelsIds = ref<number[]>([]);
const tradeoffExplanations = ref<{ reportId: string; explanation: string }[]>([]);

async function loadReports() {
  isLoading.value = true;

  // get all concerned ids
  const ids = ([] as LocationQueryValue[])
    .concat(route.query.reportId)
    .map((v) => (v ? parseInt(v) : null))
    .filter((v) => v !== null);

  // fetch reports and filter potentially not found
  const results = (
    await Promise.all(
      ids.map((id) => api.fetchEstimatorReport(tenantName.value, projectName.value, id))
    )
  ).filter((r) => r !== null);
  reports.value = results;

  // render
  isLoading.value = false;
}

async function saveComparison() {
  const [confirmed, name] = await modal.prompt({
    title: "Add a name to your comparison",
    titleIcon: "icon-cube",
    promptedValueName: "Name",
  });
  if (confirmed) {
    const result = await api.createComparisons(
      tenantName.value,
      projectName.value,
      name,
      reports.value.map((r) => r.id),
      selectedModelsIds.value,
      Object.fromEntries(tradeoffExplanations.value.map((e) => [e.reportId, e.explanation]))
    );
    if (result) {
      router.push({
        name: RouteNames.ProjectDetail,
        params: {
          tenantName: tenantName.value,
          projectName: projectName.value,
          section: ProjectDetailSections.Comparisons,
        },
      });
    }
  }
}

onMounted(async () => {
  await loadReports();
});
</script>

<template>
  <DefaultPageLayout
    :breadcrumb-items="[
      {
        label: tenantName,
        to: { name: RouteNames.Projects, params: { tenantName } },
        icon: 'icon-masonry',
      },
      {
        label: projectName,
        to: { name: RouteNames.ProjectDetail, params: { tenantName, projectName } },
        icon: 'icon-punch-card',
        iconColor: 'var(--color-background-purple)',
      },
      {
        label: 'Comparison',
        icon: 'icon-scale',
      },
    ]"
  >
    <template #default>
      <div class="comparison-builder">
        <div class="back label-s" @click="router.back()">
          <i class="icon icon-arrow-left" /> Model finder
        </div>
        <div v-if="isLoading" class="loading">
          <SkoreSpinner />
        </div>
        <div v-else class="builder">
          <EstimatorReportComparison
            :reports="reports"
            :is-read-only="false"
            v-model:tradeoff-explanations="tradeoffExplanations"
            v-bind:selected-models-ids="selectedModelsIds"
          />
        </div>
      </div>
    </template>
    <template #bottomBanner>
      <div class="comparison-save">
        <SimpleButton
          label="Save comparison"
          @click="saveComparison"
          :disabled="selectedModelsIds.length === 0"
        />
      </div>
    </template>
  </DefaultPageLayout>
</template>

<style scoped>
.comparison-builder {
  display: flex;
  height: var(--page-remaining-height);
  flex-direction: column;
  gap: var(--spacing-16);

  .back {
    color: var(--color-text-secondary);
    cursor: pointer;

    .icon {
      margin-right: var(--spacing-8);
      vertical-align: middle;
    }
  }

  .loading {
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
  }

  .builder {
    overflow: hidden;
    height: 100%;
    flex-grow: 1;
  }
}

.comparison-save {
  text-align: right;
}
</style>
